import React, { FC } from 'react';
import useMedia from 'hooks/useMedia';
import { useTranslation } from 'react-i18next';
import PageLayout from 'components/common/PageLayout';
import PageLoading from 'components/common/PageLoading';
import Typography from 'components/common/Typography';
import Link from 'components/common/Link';
import Space from 'components/common/Space';
import {
  LinksListContainer,
  LinksListItem,
} from 'components/common/LinksList/LinksList.style';
import { Button, useModalContext } from '@resideo/blueprint-react';
import { REQUEST_MY_DATA } from 'config/flags';
import { DeleteUserModal } from './DeleteUser/DeleteUserModal';
import { useNavigate } from 'react-router-dom';
import { UnableToDeleteUserModal } from './DeleteUser/UnableToDeleteUserModal';
import { DeleteUserModalError } from './DeleteUser/DeleteUserModalError';
import { useCloseAccount } from 'hooks/useCloseAccount';
import { useRisClient } from 'hooks/useRisClient';
import { useMutation } from '@tanstack/react-query';
import useRegion from 'hooks/useRegion';
import { RisError } from 'types/types';
import { RequestMyDataModal } from './RequestMyData/RequestMyDataModal';
import { RequestMyDataErrorModal } from './RequestMyData/RequestMyDataErrorModal';

export enum DeleteAccountModals {
  DeleteAccount,
  CannotDeleteAccount,
  DeleteAccountErrorModal,
}

const { Title, Text } = Typography;

const PrivacyScreen: FC = () => {
  const navigate = useNavigate();

  const isMobile = useMedia('(max-width: 62rem)');

  const { t } = useTranslation();
  const { openModal, closeModal } = useModalContext();

  const { isEUUser, loading } = useRegion();

  const { client } = useRisClient();

  const deleteUser = useMutation({
    mutationFn: async () => {
      return client.myUsers.myUsersDeleteMyUser();
    },
    onError: (resp: RisError) => {
      // The mutation has terminated with a specific error saying the user cannot be deleted
      if (resp.status === 409) {
        const message = resp.body?.errors?.[0]?.message;
        openDeleteUserModal(DeleteAccountModals.CannotDeleteAccount, message);
      } else {
        // The mutation has terminated with an error, not specific to the user getting deleted
        openDeleteUserModal(DeleteAccountModals.DeleteAccountErrorModal);
      }
    },
    onSuccess: () => {
      // The mutation executed successfully and the user gets deleted
      navigate('/sign-out');
    },
  });

  const openDeleteUserModal = (
    modalVersion: DeleteAccountModals,
    message?: string
  ) => {
    let children: React.JSX.Element;
    switch (modalVersion) {
      case DeleteAccountModals.DeleteAccount:
        children = (
          <DeleteUserModal
            onCancel={closeModal}
            onSubmit={() => deleteUser.mutate()}
          />
        );

        break;

      case DeleteAccountModals.CannotDeleteAccount:
        children = (
          <UnableToDeleteUserModal onCancel={closeModal} message={message} />
        );

        break;

      case DeleteAccountModals.DeleteAccountErrorModal:
        children = <DeleteUserModalError onCancel={closeModal} />;

        break;
    }

    if (!children) return;

    openModal({
      title: '',
      testAttribute: { 'data-test-delete-user-modal': true },
      children: children,
    });
  };

  const openRequestMyDataErrorModal = (message: string) => {
    openModal({
      title: '',
      testAttribute: { 'data-test-request-my-data-error-modal': true },
      children: (
        <RequestMyDataErrorModal message={message} onCancel={closeModal} />
      ),
    });
  };

  const closeAccount = useCloseAccount({
    onError: () =>
      openDeleteUserModal(DeleteAccountModals.DeleteAccountErrorModal),
    onConflict: openRequestMyDataErrorModal,
  });

  const openRequestMyData = () => {
    openModal({
      title: '',
      testAttribute: { 'data-test-request-my-data-modal': true },
      children: (
        <RequestMyDataModal
          closeModal={closeModal}
          onCloseAccount={() => closeAccount.mutate()}
        />
      ),
    });
  };

  return loading ? (
    <PageLoading />
  ) : (
    <>
      <PageLayout data-test-privacy-and-data-title title={t('privacy.title')}>
        <Space size={isMobile ? 18 : 32} />
        <Title level={2}>{t('privacy.subHeading')}</Title>
        <Text>{t('privacy.label')}</Text>
        <LinksListContainer>
          <LinksListItem>
            {
              <Link
                target='_blank'
                href='https://www.resideo.com/us/en/corporate/legal/privacy/english/'>
                {t('privacy.securityStatement')}
              </Link>
            }
          </LinksListItem>
          <LinksListItem>
            {
              <Link
                target='_blank'
                href='https://www.resideo.com/us/en/corporate/legal/eula/english-us/'>
                {t('privacy.websitePrivacy')}
              </Link>
            }
          </LinksListItem>
        </LinksListContainer>

        {isEUUser ? (
          <>
            <Space size={isMobile ? 18 : 32} />
            <Title data-test-data-stream-title level={2}>
              {t('privacy.dataStream.title')}
            </Title>
            <Text data-test-data-stream-description>
              {t('privacy.dataStream.description')}
            </Text>
            <LinksListContainer>
              <LinksListItem>
                <Link
                  data-test-data-stream-action
                  href='/privacy-and-data/data-stream'>
                  {t('privacy.dataStream.action')}
                </Link>
              </LinksListItem>
            </LinksListContainer>
          </>
        ) : null}

        {REQUEST_MY_DATA ? (
          <>
            <Space size={isMobile ? 18 : 32} />
            <Title data-test-request-my-data-title level={2}>
              {t('privacy.requestMyData.title')}
            </Title>

            <Link data-test-request-my-data-action onClick={openRequestMyData}>
              {t('privacy.requestMyData.submitRequest')}
            </Link>

            <Text data-test-request-my-data-description>
              {t('privacy.requestMyData.description')}
            </Text>

            <Space size={isMobile ? 18 : 32} />
            <Title data-test-data-requests-title level={2}>
              {t('privacy.dataRequests.title')}
            </Title>

            <Text data-test-data-requests-description>
              {t('privacy.dataRequests.description')}
            </Text>

            <LinksListContainer>
              <LinksListItem>
                <Link
                  data-test-data-requests-action
                  href='/privacy-and-data/data-requests'>
                  {t('privacy.dataRequests.downloadData')}
                </Link>
              </LinksListItem>
            </LinksListContainer>
          </>
        ) : null}

        <Space size={isMobile ? 18 : 32} />
        <Title data-test-delete-account-title level={2}>
          {t('privacy.deleteAccount')}
        </Title>
        <Text>
          <Text data-test-delete-account-description marginBottom='medium'>
            {t('privacy.deleteAccountDescription')}
          </Text>
          <Button
            data-test-delete-account-button
            variant='destructivePrimary'
            onClick={() => {
              openDeleteUserModal(DeleteAccountModals.DeleteAccount);
            }}>
            {t('privacy.deleteResideoId')}
          </Button>
        </Text>
      </PageLayout>
    </>
  );
};

export default PrivacyScreen;
